import React, { useState } from 'react';
import { TextField, Box, Chip, Autocomplete, Stack, IconButton, Tooltip, Button, ButtonGroup } from '@mui/material';
import { CalendarToday } from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { RangeQueryValue, SizeQueryValue, RegexpQueryValue } from '@/api/bol';
import type { QueryOperator } from '@/utils/query-builder';

export interface ValueInputProps {
  operator?: QueryOperator;
  value?: unknown;
  onChange: (value: unknown) => void;
  disabled?: boolean;
  error?: boolean;
  size?: 'small' | 'medium';
}

interface RangeInputProps {
  value: string | number;
  onChange: (value: string | number) => void;
  label: string;
  placeholder: string;
  disabled?: boolean;
  error?: boolean;
  size?: 'small' | 'medium';
  isGreaterThan: boolean;
  isInclusive: boolean;
  onToggleInclusive: () => void;
  isNumeric: boolean;
  onToggleNumeric: () => void;
}

const RangeInput: React.FC<RangeInputProps> = ({
  value,
  onChange,
  label,
  placeholder,
  disabled = false,
  error = false,
  size = 'small',
  isGreaterThan,
  isInclusive,
  onToggleInclusive,
  isNumeric,
  onToggleNumeric
}) => {
  const [showDatePicker, setShowDatePicker] = useState(false);

  const handleDateChange = (newDate: Date | null) => {
    if (newDate) {
      onChange(newDate.toISOString());
    }
    // Don't close here - let the user finish selecting time
  };

  const handleDateAccept = (newDate: Date | null) => {
    if (newDate) {
      onChange(newDate.toISOString());
    }
    setShowDatePicker(false);
  };

  const handleInputChange = (newValue: string) => {
    if (isNumeric) {
      // In numeric mode, allow partial input during typing
      if (newValue === '') {
        onChange('');
      } else if (newValue === '-' || newValue.endsWith('.') || newValue.endsWith('-')) {
        // Allow partial input: just minus sign, or ending with decimal point
        onChange(newValue);
      } else {
        const numValue = Number.parseFloat(newValue);
        if (!Number.isNaN(numValue)) {
          onChange(numValue);
        } else {
          // Invalid input - still show it for error feedback
          onChange(newValue);
        }
      }
    } else {
      onChange(newValue);
    }
  };

  // Check if we have an invalid number in numeric mode
  const hasValidationError = isNumeric && value !== '' && typeof value === 'string' && Number.isNaN(Number(value));

  const operatorText = isGreaterThan 
    ? (isInclusive ? 'gte' : 'gt') 
    : (isInclusive ? 'lte' : 'lt');
  
  const getOperatorTooltip = () => {
    if (isGreaterThan) {
      return isInclusive 
        ? 'Greater than or equal - Click to switch to exclusive (gt)' 
        : 'Greater than (exclusive) - Click to switch to inclusive (gte)';
    }
    return isInclusive 
      ? 'Less than or equal - Click to switch to exclusive (lt)' 
      : 'Less than (exclusive) - Click to switch to inclusive (lte)';
  };
  
  const typeTooltip = isNumeric 
    ? 'Number mode - Click to switch to text/date mode' 
    : 'Text/Date mode - Click to switch to number mode';

  // Parse value for date picker (only if it's a valid date string)
  let dateValue: Date | null = null;
  if (!isNumeric && value && typeof value === 'string') {
    const parsedDate = new Date(value);
    if (!Number.isNaN(parsedDate.getTime())) {
      dateValue = parsedDate;
    }
  }

  return (
    <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
      <Tooltip title={getOperatorTooltip()}>
        <Button
          size="small"
          onClick={onToggleInclusive}
          disabled={disabled}
          variant="outlined"
          sx={{ 
            minWidth: '48px',
            width: '48px',
            fontWeight: 'bold',
            fontSize: '0.75rem',
            flexShrink: 0,
          }}
        >
          {operatorText}
        </Button>
      </Tooltip>
      <Box sx={{ display: 'flex', width: '100%' }}>
        <TextField
          label={label}
          value={value}
          onChange={(e) => handleInputChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          error={error || hasValidationError}
          fullWidth
          size={size}
          type="text"
          inputProps={{
            inputMode: isNumeric ? 'decimal' : 'text',
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              paddingRight: 0,
              '& fieldset': {
                borderTopRightRadius: 0,
                borderBottomRightRadius: 0,
              }
            }
          }}
        />
        <ButtonGroup 
          variant="outlined" 
          size={size}
          sx={{ 
            flexShrink: 0,
          }}
        >
          {!isNumeric && (
            <Tooltip title="Select date/time">
              <IconButton
                size="small"
                onClick={() => setShowDatePicker(true)}
                disabled={disabled}
                sx={{ 
                  borderRadius: 0,
                  width: '48px',
                  border: '1px solid',
                  borderColor: error || hasValidationError ? 'error.main' : 'rgba(0, 0, 0, 0.23)',
                  borderLeft: 'none',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                    borderColor: error || hasValidationError ? 'error.main' : 'rgba(0, 0, 0, 0.23)',
                  }
                }}
              >
                <CalendarToday fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title={typeTooltip}>
            <Button
              size="small"
              onClick={onToggleNumeric}
              disabled={disabled}
              sx={{ 
                minWidth: '48px',
                width: '48px',
                fontWeight: 'bold',
                fontSize: '0.75rem',
                borderRadius: 0,
                borderTopRightRadius: '4px',
                borderBottomRightRadius: '4px',
                border: '1px solid',
                borderColor: error || hasValidationError ? 'error.main' : 'rgba(0, 0, 0, 0.23)',
                borderLeft: 'none',
                color: 'text.primary',
                '&:hover': {
                  backgroundColor: 'action.hover',
                  borderColor: error || hasValidationError ? 'error.main' : 'rgba(0, 0, 0, 0.23)',
                }
              }}
            >
              {isNumeric ? '123' : 'abc'}
            </Button>
          </Tooltip>
        </ButtonGroup>
      </Box>
      
      {/* Hidden DateTimePicker - only shows when calendar is clicked */}
      {showDatePicker && !isNumeric && (
        <Box sx={{ position: 'fixed', top: 0, left: 0, zIndex: 9999 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DateTimePicker
              value={dateValue}
              onChange={handleDateChange}
              open={showDatePicker}
              onClose={() => setShowDatePicker(false)}
              onAccept={handleDateAccept}
              slotProps={{ 
                textField: { 
                  size,
                  sx: { visibility: 'hidden', width: 0, height: 0, position: 'absolute' }
                },
                popper: {
                  placement: 'auto'
                }
              }}
            />
          </LocalizationProvider>
        </Box>
      )}
    </Box>
  );
};

export const ValueInput: React.FC<ValueInputProps> = ({
  operator,
  value,
  onChange,
  disabled = false,
  error = false,
  size = 'small'
}) => {
  // Initialize state hooks unconditionally at top level
  const initialJsonString = (operator === 'match' && value && typeof value === 'object') 
    ? JSON.stringify(value, null, 2) 
    : '{}';
  
  const [jsonString, setJsonString] = useState(initialJsonString);
  const [jsonError, setJsonError] = useState('');
  
  // State for numeric mode in range operator - ONLY controlled by user
  const [isGreaterNumericMode, setIsGreaterNumericMode] = React.useState(false);
  const [isLessNumericMode, setIsLessNumericMode] = React.useState(false);

  // No input needed for 'exists' operator
  if (operator === 'exists' || !operator) {
    return null;
  }

  // EQUALS operator
  if (operator === 'equals') {
    return (
      <TextField
        label="Value"
        value={value as string || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder='Enter value'
        disabled={disabled}
        error={error}
        fullWidth
        size={size}
      />
    );
  }

  // IN operator (array of values)
  if (operator === 'in') {
    const values = (value as string[]) || [];
    
    return (
      <Autocomplete
        multiple
        freeSolo
        value={values}
        onChange={(_event, newValue) => onChange(newValue)}
        options={[]}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => (
            <Chip label={option} {...getTagProps({ index })} key={option} size={size} />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label="Values"
            placeholder="Type and press Enter"
            error={error}
          />
        )}
        disabled={disabled}
        fullWidth
        size={size}
      />
    );
  }

  // RANGE operator (gte, lte, gt, lt)
  if (operator === 'range') {
    const rangeValue = (value as RangeQueryValue) || {};
    
    // Determine which values are set and their inclusive states
    const hasGreaterValue = rangeValue.gte !== undefined || rangeValue.gt !== undefined;
    const hasLessValue = rangeValue.lte !== undefined || rangeValue.lt !== undefined;
    
    // Default to inclusive if no value is set yet
    const isGreaterInclusive = hasGreaterValue ? rangeValue.gte !== undefined : true;
    const isLessInclusive = hasLessValue ? rangeValue.lte !== undefined : true;
    
    const greaterValue = rangeValue.gte ?? rangeValue.gt ?? '';
    const lessValue = rangeValue.lte ?? rangeValue.lt ?? '';
    
    // Use ONLY the mode state, never auto-detect from value type
    const isGreaterNumeric = isGreaterNumericMode;
    const isLessNumeric = isLessNumericMode;

    const handleGreaterChange = (newValue: string | number) => {
      const updatedRange = { ...rangeValue };
      
      // Remove both gt and gte first
      delete updatedRange.gt;
      delete updatedRange.gte;
      
      // Set the appropriate field based on inclusive state
      if (newValue !== '' && newValue !== undefined) {
        if (isGreaterInclusive) {
          updatedRange.gte = newValue;
        } else {
          updatedRange.gt = newValue;
        }
      }
      
      onChange(updatedRange);
    };

    const handleLessChange = (newValue: string | number) => {
      const updatedRange = { ...rangeValue };
      
      // Remove both lt and lte first
      delete updatedRange.lt;
      delete updatedRange.lte;
      
      // Set the appropriate field based on inclusive state
      if (newValue !== '' && newValue !== undefined) {
        if (isLessInclusive) {
          updatedRange.lte = newValue;
        } else {
          updatedRange.lt = newValue;
        }
      }
      
      onChange(updatedRange);
    };

    const toggleGreaterInclusive = () => {
      const updatedRange = { ...rangeValue };
      const currentValue = rangeValue.gte ?? rangeValue.gt;
      
      // Remove both fields
      delete updatedRange.gt;
      delete updatedRange.gte;
      
      // Always toggle and set a field, even with empty value
      if (isGreaterInclusive) {
        // Switch from gte to gt
        updatedRange.gt = currentValue ?? '';
      } else {
        // Switch from gt to gte
        updatedRange.gte = currentValue ?? '';
      }
      
      onChange(updatedRange);
    };

    const toggleLessInclusive = () => {
      const updatedRange = { ...rangeValue };
      const currentValue = rangeValue.lte ?? rangeValue.lt;
      
      // Remove both fields
      delete updatedRange.lt;
      delete updatedRange.lte;
      
      // Always toggle and set a field, even with empty value
      if (isLessInclusive) {
        // Switch from lte to lt
        updatedRange.lt = currentValue ?? '';
      } else {
        // Switch from lt to lte
        updatedRange.lte = currentValue ?? '';
      }
      
      onChange(updatedRange);
    };

    const toggleGreaterNumeric = () => {
      const updatedRange = { ...rangeValue };
      const currentValue = rangeValue.gte ?? rangeValue.gt;
      
      // Toggle the mode state
      setIsGreaterNumericMode(!isGreaterNumeric);
      
      // Remove both fields
      delete updatedRange.gt;
      delete updatedRange.gte;
      
      // Convert value type and set the field if there's a value
      if (currentValue !== undefined && currentValue !== '') {
        let newValue: string | number;
        if (isGreaterNumeric) {
          // Convert number to string - preserve exact format
          newValue = typeof currentValue === 'number' ? currentValue.toString() : currentValue;
        } else {
          // Convert string to number using parseFloat for decimal support
          const numVal = Number.parseFloat(currentValue as string);
          newValue = !Number.isNaN(numVal) ? numVal : currentValue; // Keep as string if not convertible
        }
        
        if (isGreaterInclusive) {
          updatedRange.gte = newValue;
        } else {
          updatedRange.gt = newValue;
        }
      }
      
      onChange(updatedRange);
    };

    const toggleLessNumeric = () => {
      const updatedRange = { ...rangeValue };
      const currentValue = rangeValue.lte ?? rangeValue.lt;
      
      // Toggle the mode state
      setIsLessNumericMode(!isLessNumeric);
      
      // Remove both fields
      delete updatedRange.lt;
      delete updatedRange.lte;
      
      // Convert value type and set the field if there's a value
      if (currentValue !== undefined && currentValue !== '') {
        let newValue: string | number;
        if (isLessNumeric) {
          // Convert number to string - preserve exact format
          newValue = typeof currentValue === 'number' ? currentValue.toString() : currentValue;
        } else {
          // Convert string to number using parseFloat for decimal support
          const numVal = Number.parseFloat(currentValue as string);
          newValue = !Number.isNaN(numVal) ? numVal : currentValue; // Keep as string if not convertible
        }
        
        if (isLessInclusive) {
          updatedRange.lte = newValue;
        } else {
          updatedRange.lt = newValue;
        }
      }
      
      onChange(updatedRange);
    };

    return (
      <Box sx={{ width: '100%' }}>
        <Stack spacing={2}>
          <RangeInput
            value={greaterValue}
            onChange={handleGreaterChange}
            label="From (minimum value)"
            placeholder="Enter minimum value"
            disabled={disabled}
            error={error}
            size={size}
            isGreaterThan={true}
            isInclusive={hasGreaterValue ? isGreaterInclusive : true}
            onToggleInclusive={toggleGreaterInclusive}
            isNumeric={isGreaterNumeric}
            onToggleNumeric={toggleGreaterNumeric}
          />
          <RangeInput
            value={lessValue}
            onChange={handleLessChange}
            label="To (maximum value)"
            placeholder="Enter maximum value"
            disabled={disabled}
            error={error}
            size={size}
            isGreaterThan={false}
            isInclusive={hasLessValue ? isLessInclusive : true}
            onToggleInclusive={toggleLessInclusive}
            isNumeric={isLessNumeric}
            onToggleNumeric={toggleLessNumeric}
          />
        </Stack>
      </Box>
    );
  }

  // SIZE operator (for arrays - gte, lte, gt, lt)
  if (operator === 'size') {
    const sizeValue = (value as SizeQueryValue) || {};

    return (
      <Box sx={{ width: '100%' }}>
        <Stack direction="row" spacing={1}>
          <TextField
            label="Min Size (gte)"
            type="number"
            value={sizeValue.gte ?? ''}
            onChange={(e) =>
              onChange({ ...sizeValue, gte: e.target.value ? Number(e.target.value) : undefined })
            }
            placeholder="0"
            disabled={disabled}
            error={error}
            fullWidth
            size={size}
          />
          <TextField
            label="Max Size (lte)"
            type="number"
            value={sizeValue.lte ?? ''}
            onChange={(e) =>
              onChange({ ...sizeValue, lte: e.target.value ? Number(e.target.value) : undefined })
            }
            placeholder="100"
            disabled={disabled}
            error={error}
            fullWidth
            size={size}
          />
        </Stack>
        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
          <TextField
            label="Greater Than (gt)"
            type="number"
            value={sizeValue.gt ?? ''}
            onChange={(e) =>
              onChange({ ...sizeValue, gt: e.target.value ? Number(e.target.value) : undefined })
            }
            placeholder="Exclusive min"
            disabled={disabled}
            error={error}
            fullWidth
            size={size}
          />
          <TextField
            label="Less Than (lt)"
            type="number"
            value={sizeValue.lt ?? ''}
            onChange={(e) =>
              onChange({ ...sizeValue, lt: e.target.value ? Number(e.target.value) : undefined })
            }
            placeholder="Exclusive max"
            disabled={disabled}
            error={error}
            fullWidth
            size={size}
          />
        </Stack>
      </Box>
    );
  }

  // REGEXP operator (pattern + flags)
  if (operator === 'regexp') {
    const regexpValue = (value as RegexpQueryValue) || { value: '', flags: undefined };

    return (
      <Box sx={{ width: '100%' }}>
        <Stack direction="row" spacing={1}>
          <TextField
            label="Pattern"
            value={regexpValue.value || ''}
            onChange={(e) =>
              onChange({ ...regexpValue, value: e.target.value })
            }
            placeholder="^[A-Z].*"
            disabled={disabled}
            error={error}
            fullWidth
            size={size}
          />
          <TextField
            label="Flags"
            value={regexpValue.flags || ''}
            onChange={(e) =>
              onChange({ ...regexpValue, flags: e.target.value || undefined })
            }
            placeholder="i"
            disabled={disabled}
            error={error}
            sx={{ minWidth: '120px' }}
            size={size}
          />
        </Stack>
      </Box>
    );
  }

  // MATCH operator (JSON object/array matching)
  if (operator === 'match') {
    const handleChange = (newValue: string) => {
      setJsonString(newValue);
      try {
        const parsed = JSON.parse(newValue);
        onChange(parsed);
        setJsonError('');
      } catch {
        setJsonError('Invalid JSON');
      }
    };

    return (
      <Box>
        <TextField
          label="Match Object (JSON)"
          value={jsonString}
          onChange={(e) => handleChange(e.target.value)}
          placeholder='{"key": "value"}'
          disabled={disabled}
          error={error || !!jsonError}
          helperText={jsonError}
          fullWidth
          multiline
          rows={4}
          size={size}
        />
      </Box>
    );
  }

  return null;
};
